import { useState } from 'react';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { BarStack } from '@visx/shape';

import type { UiSpendSeries } from '@nightshift/ui-types';

import { tokens, usd } from '../lib/format.ts';
import {
  buildSpendPlot,
  spendChartNote,
  stackKeys,
  type SpendDay,
  type SpendDimension,
  type SpendMeasure,
} from '../lib/spend-series.ts';
import { ChartDayAxis, ChartGrid, ChartValueAxis, dayLabelInterval } from './chart/axes.tsx';
import { ChartLegend } from './chart/legend.tsx';
import { plotBox, useContainerWidth } from './chart/plot.tsx';
import './chart/chart.css';
import './spend-chart.css';

const HEIGHT = 216;
// Bottom clears the day labels: an SVG clips its own overflow, so a tight padding
// shaves the descenders off "Sept" rather than letting them hang.
const PADDING = { top: 10, right: 6, bottom: 28, left: 46 };
/** A 2px surface gap between stacked segments, so adjoining hues never touch. */
const SEGMENT_GAP = 2;

function formatValue(value: number, measure: SpendMeasure): string {
  return measure === 'usd' ? usd(value) : tokens(value);
}

/** Axis figures stay terse — `$12` not `$12.00`, `40k` not `40000`. */
function formatAxis(value: number, measure: SpendMeasure): string {
  if (value === 0) return '0';
  if (measure === 'tokens') return tokens(value);
  return value >= 1 ? `$${Math.round(value)}` : `$${value.toFixed(2)}`;
}

function dayLabel(day: string): string {
  const at = Date.parse(`${day}T00:00:00.000Z`);
  if (!Number.isFinite(at)) return day;
  return new Date(at).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** What a screen reader hears for one column. */
function describeDay(day: SpendDay, measure: SpendMeasure, order: readonly string[]): string {
  if (day.total === 0) {
    return `${dayLabel(day.day)}: nothing recorded`;
  }
  const parts = order
    .filter((key) => (day.values[key] ?? 0) > 0)
    .map((key) => `${key} ${formatValue(day.values[key]!, measure)}`);
  return `${dayLabel(day.day)}: ${formatValue(day.total, measure)} — ${parts.join(', ')}`;
}

export function SpendChart({
  series,
  dimension,
  measure,
}: {
  series: UiSpendSeries;
  dimension: SpendDimension;
  measure: SpendMeasure;
}) {
  const { ref, width } = useContainerWidth();
  const [active, setActive] = useState<string | null>(null);

  const plot = buildSpendPlot(series, dimension, measure);
  const keys = stackKeys(plot);
  const box = plotBox(width, HEIGHT, PADDING);

  const xScale = scaleBand<string>({
    domain: plot.days.map((day) => day.day),
    range: [0, box.innerWidth],
    paddingInner: 0.28,
    paddingOuter: 0.1,
  });
  // A flat-zero window still needs a sane axis, so an empty plot gets a unit domain.
  const yScale = scaleLinear<number>({
    domain: [0, plot.max > 0 ? plot.max : 1],
    range: [box.innerHeight, 0],
    nice: true,
  });

  const colours = new Map(plot.keys.map((entry) => [entry.key, entry.colour]));
  const labels = new Map(plot.keys.map((entry) => [entry.key, entry.label]));
  const activeDay = active ? plot.days.find((day) => day.day === active) : undefined;
  const note = spendChartNote(series, plot);

  return (
    <div className="spend-chart">
      <div className="chart-plot" ref={ref} onMouseLeave={() => setActive(null)}>
        {width > 0 ? (
          <>
            <svg className="chart-svg" width={width} height={HEIGHT} aria-hidden="true">
              <Group left={PADDING.left} top={PADDING.top}>
                <ChartGrid box={box} scale={yScale} />
                <ChartValueAxis scale={yScale} format={(value) => formatAxis(value, measure)} />
                <ChartDayAxis
                  box={box}
                  scale={xScale}
                  interval={dayLabelInterval(plot.days.length, box.innerWidth)}
                  format={dayLabel}
                />
                {plot.empty ? null : (
                  <BarStack<SpendDay, string>
                    data={plot.days}
                    keys={keys}
                    value={(day, key) => day.values[key] ?? 0}
                    x={(day) => day.day}
                    xScale={xScale}
                    yScale={yScale}
                    color={(key) => colours.get(key) ?? 'var(--text-4)'}
                  >
                    {(stacks) =>
                      stacks.map((stack) =>
                        stack.bars.map((bar) => {
                          if (bar.height <= 0) return null;
                          // Take the gap off the top of each segment, never off the
                          // baseline one's foot, so the stack still sits on zero.
                          const height = Math.max(1, bar.height - SEGMENT_GAP);
                          return (
                            <rect
                              key={`${stack.key}-${bar.index}`}
                              className={`spend-bar${active === plot.days[bar.index]?.day ? ' is-active' : ''}`}
                              x={bar.x}
                              y={bar.y + (bar.height - height)}
                              width={bar.width}
                              height={height}
                              fill={bar.color}
                            />
                          );
                        }),
                      )
                    }
                  </BarStack>
                )}
              </Group>
            </svg>

            {/*
              One hit target per column, the full height of the plot. Keyboard users
              tab the window day by day and hear the same breakdown the tooltip shows.
            */}
            {plot.days.map((day) => {
              const left = PADDING.left + (xScale(day.day) ?? 0);
              const bandWidth = xScale.bandwidth();
              return (
                <button
                  key={day.day}
                  type="button"
                  className="chart-hit"
                  style={{
                    left: left - xScale.step() * 0.14,
                    width: bandWidth + xScale.step() * 0.28,
                    height: box.innerHeight + PADDING.top,
                  }}
                  aria-label={describeDay(day, measure, keys)}
                  onMouseEnter={() => setActive(day.day)}
                  onFocus={() => setActive(day.day)}
                  onBlur={() => setActive(null)}
                />
              );
            })}

            {activeDay ? (
              <SpendTip
                day={activeDay}
                measure={measure}
                order={keys}
                colours={colours}
                labels={labels}
                left={PADDING.left + (xScale(activeDay.day) ?? 0) + xScale.bandwidth() / 2}
                width={width}
              />
            ) : null}
          </>
        ) : null}
      </div>

      <ChartLegend
        entries={plot.keys.map((entry) => ({
          key: entry.key,
          label: entry.label,
          colour: entry.colour,
          // An all-unpriced series reads "unknown"; $0.00 would claim it was free.
          value: entry.priced ? formatValue(entry.total, measure) : 'unknown',
        }))}
      />
      <p className="spend-chart-note">{note}</p>
    </div>
  );
}

function SpendTip({
  day,
  measure,
  order,
  colours,
  labels,
  left,
  width,
}: {
  day: SpendDay;
  measure: SpendMeasure;
  order: readonly string[];
  colours: Map<string, string>;
  labels: Map<string, string>;
  left: number;
  width: number;
}) {
  const rows = order.filter((key) => (day.values[key] ?? 0) > 0);
  // Keep the card inside the panel rather than letting it run off a narrow column.
  const clamped = Math.min(Math.max(left, 74), Math.max(74, width - 74));
  return (
    <div className="chart-tip" style={{ left: clamped, transform: 'translateX(-50%)', top: 4 }}>
      <div className="chart-tip-head">
        <span>{dayLabel(day.day)}</span>
        <span className="chart-tip-total num">{formatValue(day.total, measure)}</span>
      </div>
      {rows.length === 0 ? (
        <p className="chart-tip-note">
          {day.entries === 0 ? 'Nothing recorded' : 'Recorded, but no priced entry'}
        </p>
      ) : (
        rows.map((key) => (
          <div className="chart-tip-row" key={key}>
            <span className="chart-legend-swatch" style={{ background: colours.get(key) }} aria-hidden="true" />
            <span className="truncate">{labels.get(key) ?? key}</span>
            <span className="chart-tip-value num">{formatValue(day.values[key]!, measure)}</span>
          </div>
        ))
      )}
      {measure === 'usd' && day.unpriced > 0 ? (
        <p className="chart-tip-note">
          {day.unpriced} unpriced {day.unpriced === 1 ? 'entry' : 'entries'} not shown
        </p>
      ) : null}
    </div>
  );
}
